/**
 * Curated "common care" billing codes shown at the top of a hospital's price
 * list — the handful of services a consumer actually shops for (ER visits,
 * imaging, lab tests, everyday surgeries, common inpatient stays).
 *
 * The list is deliberately short and stable. Friendly names live in
 * hospital.json under mappingsTable.commonCare.names.<family>_<code> so they
 * can be translated; codes_stats_<family>.json (R2) supplies the U.S. median /
 * p10 / p90 for each code.
 */

export type CommonCareFamily = 'cpt' | 'drg';
export type CommonCareGroup = 'er' | 'imaging' | 'lab' | 'procedure' | 'stay';

export interface CommonCareCode {
  family: CommonCareFamily;
  code: string;
  group: CommonCareGroup;
}

/** Display order of the groups (and of the rows inside the card). */
export const COMMON_CARE_GROUPS: CommonCareGroup[] = ['er', 'imaging', 'lab', 'procedure', 'stay'];

const cpt = (code: string, group: CommonCareGroup): CommonCareCode => ({ family: 'cpt', code, group });
const drg = (code: string, group: CommonCareGroup): CommonCareCode => ({ family: 'drg', code, group });

export const COMMON_CARE_CODES: CommonCareCode[] = [
  // Emergency & clinic visits
  cpt('99283', 'er'),
  cpt('99284', 'er'),
  cpt('99285', 'er'),
  cpt('99213', 'er'),
  cpt('99214', 'er'),
  // Imaging
  cpt('71046', 'imaging'),
  cpt('70450', 'imaging'),
  cpt('74177', 'imaging'),
  cpt('70553', 'imaging'),
  cpt('72148', 'imaging'),
  cpt('73721', 'imaging'),
  cpt('76700', 'imaging'),
  cpt('77067', 'imaging'),
  // Lab tests
  cpt('36415', 'lab'),
  cpt('85025', 'lab'),
  cpt('80053', 'lab'),
  cpt('80048', 'lab'),
  cpt('80061', 'lab'),
  cpt('81001', 'lab'),
  cpt('84443', 'lab'),
  cpt('83036', 'lab'),
  // Common procedures
  cpt('45378', 'procedure'),
  cpt('43239', 'procedure'),
  cpt('47562', 'procedure'),
  cpt('29881', 'procedure'),
  cpt('66984', 'procedure'),
  cpt('20610', 'procedure'),
  cpt('12001', 'procedure'),
  cpt('29125', 'procedure'),
  // Hospital stays (MS-DRG bundles — the whole admission)
  drg('470', 'stay'),
  drg('807', 'stay'),
  drg('788', 'stay'),
  drg('871', 'stay'),
  drg('193', 'stay'),
  drg('291', 'stay'),
  drg('392', 'stay'),
  drg('690', 'stay'),
  drg('064', 'stay'),
];

/** MS-DRG codes appear as "470", "0470", "MS-DRG 470" … — compare on the padded 3-digit form. */
export function normalizeDrg(code: string | null | undefined): string {
  const digits = (code || '').replace(/\D/g, '');
  return digits ? digits.slice(-3).padStart(3, '0') : '';
}
